import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams, Link, Navigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const emailFromQuery = (
    searchParams.get('email') ||
    searchParams.get('user_email') ||
    searchParams.get('platform_email') ||
    ''
  ).trim();
  
  // Get the location they were trying to go to
  const from = location.state?.from?.pathname || '/dashboard';

  const readStoredPrefillEmail = () => {
    const sessionValue = (sessionStorage.getItem('easyai_prefill_email') || '').trim();
    if (sessionValue) return sessionValue;
    return (localStorage.getItem('easyai_prefill_email') || '').trim();
  };

  const persistPrefillEmail = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    sessionStorage.setItem('easyai_prefill_email', trimmed);
    localStorage.setItem('easyai_prefill_email', trimmed);
  };

  useEffect(() => {
    const routeStateEmail = ((location.state as any)?.email || '').trim();
    const rememberedEmail = readStoredPrefillEmail();
    const prefillEmail = emailFromQuery || routeStateEmail || rememberedEmail;

    if (!emailTouched && prefillEmail && !email) {
      setEmail(prefillEmail);
      persistPrefillEmail(prefillEmail);
    }
  }, [emailFromQuery, email, emailTouched, location.state]);

  useEffect(() => {
    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      const eventEmail = (
        data?.email ||
        data?.user_email ||
        data?.platform_email ||
        ''
      ).trim();

      const messageType = (data?.type || '').toString();
      const isPrefillMessage =
        messageType === 'easyai-prefill-email' ||
        messageType === 'platform-auth-context' ||
        messageType === 'easyai-auth-context';

      if (!isPrefillMessage || !eventEmail) return;

      persistPrefillEmail(eventEmail);
      if (!emailTouched) {
        setEmail(eventEmail);
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [emailTouched]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'Failed to log in');
    } finally {
      setLoading(false);
    }
  };

  // Already signed in (e.g. a tab that briefly saw a sign-out): continue where it was going.
  if (!authLoading && user) {
    return <Navigate to={from} replace />;
  }

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h5" align="center" gutterBottom>
            Sign In
          </Typography>
          
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => {
                setEmailTouched(true);
                setEmail(e.target.value);
              }}
              disabled={loading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
            />
            <Box sx={{ textAlign: 'right', mt: 1, mb: 2 }}>
              <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
                <Typography variant="body2" sx={{ cursor: 'pointer', color: '#1976d2', '&:hover': { textDecoration: 'underline' } }}>
                  Forgot password?
                </Typography>
              </Link>
            </Box>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Sign In'}
            </Button>
          </Box>
          
          <Divider sx={{ my: 2 }}>OR</Divider>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2">
              Don't have an account?{' '}
              <Link to="/signup" style={{ textDecoration: 'none' }}>
                Sign up
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;
